import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaGithub, FaExternalLinkAlt, FaShieldAlt, FaBrain, FaCar, FaTrash } from 'react-icons/fa';
import './Projects.css';

const Projects = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const projects = [
    {
      id: 1,
      title: 'Vulnerability Classifier & Remediation AI',
      description: 'Advanced AI/ML system that automatically classifies security vulnerabilities and provides intelligent remediation suggestions. Uses machine learning algorithms to analyze vulnerability patterns and recommend appropriate fixes.',
      icon: FaShieldAlt,
      technologies: ['Python', 'Machine Learning', 'TensorFlow', 'Scikit-learn', 'Flask'],
      features: [
        'Automated vulnerability classification',
        'AI-powered remediation suggestions',
        'Real-time threat analysis',
        'Integration with security scanners'
      ],
      github: 'https://github.com/ShahzaibRK',
      demo: '#',
      status: 'Completed'
    },
    {
      id: 2,
      title: 'Movie Genre Predictor with Translation',
      description: 'Intelligent movie genre prediction system with multi-language translation capabilities. Analyzes movie descriptions and metadata to predict genres while supporting multiple languages for global accessibility.',
      icon: FaBrain,
      technologies: ['Python', 'NLP', 'Machine Learning', 'Google Translate API', 'React'],
      features: [
        'Genre prediction using NLP',
        'Multi-language support',
        'Real-time translation',
        'Interactive web interface'
      ],
      github: 'https://github.com/ShahzaibRK',
      demo: '#',
      status: 'Completed'
    },
    {
      id: 3,
      title: 'Vehicle Sales Management System',
      description: 'Comprehensive vehicle sales and management platform similar to PakWheels. Features secure user authentication, vehicle listings, search functionality, and transaction management with modern UI/UX.',
      icon: FaCar,
      technologies: ['React', 'Node.js', 'MongoDB', 'Express', 'JWT', 'Stripe'],
      features: [
        'User authentication & profiles',
        'Vehicle listing & search',
        'Secure payment integration',
        'Admin dashboard'
      ],
      github: 'https://github.com/ShahzaibRK',
      demo: '#',
      status: 'In Progress'
    },
    {
      id: 4,
      title: 'Secure File Shredder',
      description: 'Advanced file deletion tool that prevents data recovery by securely overwriting file data multiple times. Implements military-grade deletion standards to ensure complete data destruction.',
      icon: FaTrash,
      technologies: ['C++', 'File System APIs', 'Cryptography', 'Qt', 'Windows API'],
      features: [
        'Military-grade file deletion',
        'Multiple overwrite passes',
        'Secure directory wiping',
        'Recovery prevention'
      ],
      github: 'https://github.com/ShahzaibRK',
      demo: '#',
      status: 'Completed'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="projects" className="projects" ref={ref}>
      <div className="container">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.h2 className="section-title" variants={itemVariants}>
            Featured Projects
          </motion.h2>

          <div className="projects-grid">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                className="project-card"
                variants={itemVariants}
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="project-header">
                  <motion.div 
                    className="project-icon"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <project.icon />
                  </motion.div>
                  <div className="project-status">
                    <span className={`status-badge ${project.status.toLowerCase().replace(' ', '-')}`}>
                      {project.status}
                    </span>
                  </div>
                </div>

                <div className="project-content">
                  <h3 className="project-title">{project.title}</h3>
                  <p className="project-description">{project.description}</p>

                  <div className="project-features">
                    <h4>Key Features:</h4>
                    <ul>
                      {project.features.map((feature, featureIndex) => (
                        <motion.li
                          key={featureIndex}
                          initial={{ opacity: 0, x: -10 }}
                          animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
                          transition={{ delay: 0.5 + index * 0.1 + featureIndex * 0.05 }}
                        >
                          {feature}
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  <div className="project-technologies">
                    {project.technologies.map((tech, techIndex) => (
                      <motion.span
                        key={tech}
                        className="tech-tag"
                        initial={{ opacity: 0, scale: 0 }}
                        animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }}
                        transition={{ delay: 0.7 + index * 0.1 + techIndex * 0.03 }}
                        whileHover={{ scale: 1.1 }}
                      >
                        {tech}
                      </motion.span>
                    ))}
                  </div>
                </div>

                <div className="project-links">
                  <motion.a
                    href={project.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="project-link github"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaGithub />
                    <span>Code</span>
                  </motion.a>
                  <motion.a
                    href={project.demo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="project-link demo"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaExternalLinkAlt />
                    <span>Demo</span>
                  </motion.a>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div className="projects-cta" variants={itemVariants}>
            <h3>Want to see more?</h3>
            <p>Check out my GitHub for additional projects and contributions</p>
            <motion.a
              href="https://github.com/ShahzaibRK"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaGithub />
              View All Projects
            </motion.a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;